<template>
  <el-container class="admin-shell" :style="{ '--sidebar-width': collapsed ? '64px' : '224px' }">
    <el-aside :width="collapsed ? '64px' : '224px'" class="sidebar">
      <div class="sidebar-brand">
        <span class="brand-mark small">L</span>
        <div v-show="!collapsed">
          <small>客户自助后台</small>
        </div>
      </div>
      <el-menu :default-active="'records'" :collapse="collapsed">
        <div v-show="!collapsed" class="menu-group-label">我的业务</div>
        <el-menu-item index="records">
          <el-icon><Tickets /></el-icon>
          <span>广告记录</span>
        </el-menu-item>
      </el-menu>
      <div class="user-box">
        <el-avatar :size="34">{{ (customer.phone.value || '客').slice(-2) }}</el-avatar>
        <div v-show="!collapsed">
          <strong>{{ customer.phone.value || '客户' }}</strong>
          <small>客户账号</small>
        </div>
        <el-tooltip content="退出登录" placement="top">
          <el-button text circle @click="onLogout">
            <el-icon><SwitchButton /></el-icon>
          </el-button>
        </el-tooltip>
      </div>
    </el-aside>

    <el-container>
      <el-header class="topbar">
        <div>
          <h1>广告记录</h1>
          <p>投放记录与金币明细 · {{ today }}</p>
        </div>
        <div class="topbar-actions">
          <el-button text @click="collapsed = !collapsed">
            <el-icon><Expand v-if="collapsed" /><Fold v-else /></el-icon>
          </el-button>
          <el-button type="primary" plain :loading="customer.loading.value" @click="customer.loadAll(true)">
            <el-icon><Refresh /></el-icon>刷新数据
          </el-button>
        </div>
      </el-header>

      <el-main class="main-content" v-loading="customer.loading.value">
        <section class="stat-grid customer-stats" :class="{ 'no-balance': customer.info.riskEnabled !== 1 }">
          <div class="stat-card revenue-card">
            <span class="card-icon">
              <el-icon :size="16"><Coin /></el-icon>
            </span>
            <span>总金币</span>
            <strong class="revenue-value">{{ totalRevenueFormatted }} 金币</strong>
            <small>{{ appRevenueHint }}</small>
          </div>
          <div v-if="customer.info.riskEnabled === 1" class="stat-card balance-card">
            <span class="card-icon">
              <el-icon :size="16"><Wallet /></el-icon>
            </span>
            <span>可用额度</span>
            <strong>{{ customer.info.balance || 0 }} 次</strong>
            <small>{{ customer.info.adEnabled === 1 ? '广告投放中' : '广告已暂停' }}</small>
          </div>
          <div class="stat-card">
            <span>应用总数</span>
            <strong>{{ customer.apps.value.length }}</strong>
            <small>已接入应用</small>
          </div>
          <div class="stat-card">
            <span>设备总数</span>
            <strong>{{ customer.devices.value.length }}</strong>
            <small>全部设备</small>
          </div>
        </section>

        <AdRecordsPanel
          :filters="customer.recordFilters"
          :records="customer.records.value"
          :total="customer.recordPagination.total"
          :total-reward="customer.recordPagination.totalReward"
          :page="customer.recordPagination.page"
          :page-size="customer.recordPagination.size"
          :loading="customer.recordsLoading.value"
          :groups="customer.groups.value"
          :apps="customer.apps.value"
          :unique-ad-platforms="customer.uniqueAdPlatforms.value"
          :unique-ad-types="customer.uniqueAdTypes.value"
          :on-clear-filters="customer.clearRecordFilters"
          :show-app-filter="customer.apps.value.length >= 2"
          show-app-column
          hide-total-revenue
          description="可按应用、时间、用户ID、设备ID、平台筛选；应用选「全部」时总金币为所有应用合计"
          @search="customer.searchRecords"
          @page-change="customer.onRecordPageChange"
        />
      </el-main>
    </el-container>
  </el-container>
</template>

<script setup>
import { computed, inject, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Tickets, Refresh, SwitchButton, Fold, Expand, Coin, Wallet } from '@element-plus/icons-vue'
import AdRecordsPanel from '@/components/AdRecordsPanel.vue'
import { formatReward } from '@/utils/reward'

const customer = inject('customer')
const router = useRouter()

const collapsed = ref(false)
const totalRevenueFormatted = computed(() => formatReward(customer.recordPagination.totalReward))
const appRevenueHint = computed(() => {
  const appId = customer.recordFilters.appId
  if (appId == null) {
    return customer.apps.value.length >= 2
      ? '全部应用广告记录合计'
      : '当前筛选条件下全部记录合计'
  }
  const app = customer.apps.value.find((a) => a.id === appId)
  return app ? `应用「${app.appName || app.packageName}」金币合计` : '当前应用金币合计'
})
const today = new Intl.DateTimeFormat('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }).format(new Date())

const onLogout = () => {
  customer.logout()
  router.push('/login')
}
</script>

<style scoped>
.customer-stats {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.customer-stats.no-balance {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.revenue-card,
.balance-card {
  padding-right: 52px;
}

.card-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 8px;
}

.revenue-card::before {
  background: linear-gradient(180deg, #10b981, #34d399);
}

.revenue-card .card-icon {
  color: #10b981;
  background: color-mix(in srgb, #10b981 12%, #fff);
}

.revenue-value {
  color: #dc2626;
}

.balance-card::before {
  background: linear-gradient(180deg, #f59e0b, #fbbf24);
}

.balance-card .card-icon {
  color: #f59e0b;
  background: color-mix(in srgb, #f59e0b 12%, #fff);
}

@media (max-width: 1100px) {
  .customer-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 600px) {
  .customer-stats { grid-template-columns: 1fr; }
}
</style>
